"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.groupEvents = groupEvents;
const extract_dependencies_1 = require("./extract-dependencies");
// Event Set - group of events that update the same term to run in order
// Event Collection - group of event sets that are ordered by dependencies
// This may seem weird to create a reference object for events with terms and iterables
// then later iterate over those terms and iterables to add to the larger table but that's
// because we need to create these sets of events that run in order.
function createEventSetsFromTerm(term, iterable) {
    // Create local references for the events until the loop is done
    let onReady;
    const onTermInput = {
        terms: {},
        iterables: {},
    };
    const onIterableCount = {};
    const onAction = {};
    if (!term.events) {
        return {
            onReady,
            onTermInput,
            onIterableCount,
            onAction,
        };
    }
    for (const termEvent of term.events) {
        const dependencies = extractDependenciesFromStatements(termEvent.statements, {
            term,
            iterable,
        });
        if (termEvent.on?.includes("READY")) {
            if (!onReady) {
                onReady = {
                    termName: term.name,
                    iterableName: iterable?.name,
                    dependencies,
                    events: [termEvent.statements],
                };
            }
            else {
                onReady.dependencies.push(...dependencies);
                onReady.events.push(termEvent.statements);
            }
        }
        for (const termPath of termEvent.onTermInput ?? []) {
            const [iterableName, termName] = parseTermPath(termPath, iterable?.name);
            if (iterableName) {
                if (!onTermInput.iterables[iterableName]) {
                    onTermInput.iterables[iterableName] = {};
                }
                if (!onTermInput.iterables[iterableName][termName]) {
                    onTermInput.iterables[iterableName][termName] = {
                        termName: term.name,
                        iterableName: iterable?.name,
                        dependencies,
                        events: [termEvent.statements],
                    };
                }
                else {
                    onTermInput.iterables[iterableName][termName].dependencies.push(...dependencies);
                    onTermInput.iterables[iterableName][termName].events.push(termEvent.statements);
                }
                continue;
            }
            if (!onTermInput.terms[termName]) {
                onTermInput.terms[termName] = {
                    termName: term.name,
                    iterableName: iterable?.name,
                    dependencies,
                    events: [termEvent.statements],
                };
            }
            else {
                onTermInput.terms[termName].dependencies.push(...dependencies);
                onTermInput.terms[termName].events.push(termEvent.statements);
            }
        }
        for (const iterableName of termEvent.onIterableCount ?? []) {
            if (!onIterableCount[iterableName]) {
                onIterableCount[iterableName] = {
                    termName: term.name,
                    dependencies,
                    events: [termEvent.statements],
                };
            }
            else {
                onIterableCount[iterableName].dependencies.push(...dependencies);
                onIterableCount[iterableName].events.push(termEvent.statements);
            }
        }
        for (const actionName of termEvent.onAction ?? []) {
            if (!onAction[actionName]) {
                onAction[actionName] = {
                    termName: term.name,
                    iterableName: iterable?.name,
                    dependencies,
                    events: [termEvent.statements],
                };
            }
            else {
                onAction[actionName].dependencies.push(...dependencies);
                onAction[actionName].events.push(termEvent.statements);
            }
        }
    }
    return {
        onReady,
        onTermInput,
        onIterableCount,
        onAction,
    };
}
function groupEvents(model) {
    const onReadyEvents = [];
    const onTermInputEvents = {
        terms: {},
        iterables: {},
    };
    const onIterableCountEvents = {};
    const onActionEvents = {};
    // Create a reference for sorting by dependencies
    const eventSetReference = {
        onReady: {
            terms: {},
            iterables: {},
        },
        onTermInput: {
            terms: {},
            iterables: {},
        },
        onIterableCount: {},
        onAction: {},
    };
    for (const term of Object.values(model.terms)) {
        const eventSets = createEventSetsFromTerm(term);
        if (eventSets.onReady) {
            onReadyEvents.push(eventSets.onReady);
            eventSetReference.onReady.terms[term.name] = eventSets.onReady;
        }
        for (const [termName, eventSet] of Object.entries(eventSets.onTermInput.terms)) {
            // Add the event set to the term that will trigger it
            if (!onTermInputEvents.terms[termName]) {
                onTermInputEvents.terms[termName] = [eventSet];
            }
            else {
                onTermInputEvents.terms[termName].push(eventSet);
            }
            // Add to the event set reference so the events can be sorted by dependencies
            if (!eventSetReference.onTermInput.terms[termName]) {
                eventSetReference.onTermInput.terms[termName] = {
                    terms: {},
                    iterables: {},
                };
            }
            eventSetReference.onTermInput.terms[termName].terms[term.name] = eventSet;
        }
        for (const [iterableName, termEventSets] of Object.entries(eventSets.onTermInput.iterables)) {
            if (!onTermInputEvents.iterables[iterableName]) {
                onTermInputEvents.iterables[iterableName] = {};
            }
            if (!eventSetReference.onTermInput.iterables[iterableName]) {
                eventSetReference.onTermInput.iterables[iterableName] = {};
            }
            for (const [termName, eventSet] of Object.entries(termEventSets)) {
                // Add the event set to the term that will trigger it
                if (!onTermInputEvents.iterables[iterableName][termName]) {
                    onTermInputEvents.iterables[iterableName][termName] = [eventSet];
                }
                else {
                    onTermInputEvents.iterables[iterableName][termName].push(eventSet);
                }
                // Add to the event set reference so the events can be sorted by dependencies
                if (!eventSetReference.onTermInput.iterables[iterableName][termName]) {
                    eventSetReference.onTermInput.iterables[iterableName][termName] = {
                        terms: {},
                        iterables: {},
                    };
                }
                eventSetReference.onTermInput.iterables[iterableName][termName].terms[term.name] = eventSet;
            }
        }
        for (const [iterableName, eventSet] of Object.entries(eventSets.onIterableCount)) {
            if (!eventSetReference.onIterableCount[iterableName]) {
                eventSetReference.onIterableCount[iterableName] = {
                    terms: {},
                    // we won't actually add any term-iterable references in here because
                    // a term-iterable can't listen to an iterable action
                    iterables: {},
                };
            }
            if (!onIterableCountEvents[iterableName]) {
                onIterableCountEvents[iterableName] = [eventSet];
            }
            else {
                onIterableCountEvents[iterableName].push(eventSet);
            }
            eventSetReference.onIterableCount[iterableName].terms[term.name] =
                eventSet;
        }
        for (const [actionName, eventSet] of Object.entries(eventSets.onAction)) {
            if (!eventSetReference.onAction[actionName]) {
                eventSetReference.onAction[actionName] = {
                    terms: {},
                    iterables: {},
                };
            }
            if (!onActionEvents[actionName]) {
                onActionEvents[actionName] = [eventSet];
            }
            else {
                onActionEvents[actionName].push(eventSet);
            }
            eventSetReference.onAction[actionName].terms[term.name] = eventSet;
        }
    }
    for (const iterable of Object.values(model.iterables)) {
        for (const term of Object.values(iterable.terms)) {
            const eventSets = createEventSetsFromTerm(term, iterable);
            if (eventSets.onReady) {
                onReadyEvents.push(eventSets.onReady);
                if (!eventSetReference.onReady.iterables[iterable.name]) {
                    eventSetReference.onReady.iterables[iterable.name] = {};
                }
                eventSetReference.onReady.iterables[iterable.name][term.name] =
                    eventSets.onReady;
            }
            for (const [termName, eventSet] of Object.entries(eventSets.onTermInput.terms)) {
                // Add the event set to the term that will trigger it
                if (!onTermInputEvents.terms[termName]) {
                    onTermInputEvents.terms[termName] = [eventSet];
                }
                else {
                    onTermInputEvents.terms[termName].push(eventSet);
                }
                // Add to the event set reference so the events can be sorted by dependencies
                if (!eventSetReference.onTermInput.terms[termName]) {
                    eventSetReference.onTermInput.terms[termName] = {
                        terms: {},
                        iterables: {},
                    };
                }
                if (!eventSetReference.onTermInput.terms[termName].iterables[iterable.name]) {
                    eventSetReference.onTermInput.terms[termName].iterables[iterable.name] = {};
                }
                eventSetReference.onTermInput.terms[termName].iterables[iterable.name][term.name] = eventSet;
            }
            for (const [iterableName, termEventSets] of Object.entries(eventSets.onTermInput.iterables)) {
                if (!onTermInputEvents.iterables[iterableName]) {
                    onTermInputEvents.iterables[iterableName] = {};
                }
                if (!eventSetReference.onTermInput.iterables[iterableName]) {
                    eventSetReference.onTermInput.iterables[iterableName] = {};
                }
                for (const [termName, eventSet] of Object.entries(termEventSets)) {
                    // Add the event set to the term that will trigger it
                    if (!onTermInputEvents.iterables[iterableName][termName]) {
                        onTermInputEvents.iterables[iterableName][termName] = [eventSet];
                    }
                    else {
                        onTermInputEvents.iterables[iterableName][termName].push(eventSet);
                    }
                    // Add to the event set reference so the events can be sorted by dependencies
                    if (!eventSetReference.onTermInput.iterables[iterableName][termName]) {
                        eventSetReference.onTermInput.iterables[iterableName][termName] = {
                            terms: {},
                            iterables: {},
                        };
                    }
                    if (!eventSetReference.onTermInput.iterables[iterableName][termName]
                        .iterables[iterable.name]) {
                        eventSetReference.onTermInput.iterables[iterableName][termName].iterables[iterable.name] = {};
                    }
                    eventSetReference.onTermInput.iterables[iterableName][termName].iterables[iterable.name][term.name] = eventSet;
                }
            }
            for (const [actionName, eventSet] of Object.entries(eventSets.onAction)) {
                if (!eventSetReference.onAction[actionName]) {
                    eventSetReference.onAction[actionName] = {
                        terms: {},
                        iterables: {},
                    };
                }
                if (!onActionEvents[actionName]) {
                    onActionEvents[actionName] = [eventSet];
                }
                else {
                    onActionEvents[actionName].push(eventSet);
                }
                if (!eventSetReference.onAction[actionName].iterables[iterable.name]) {
                    eventSetReference.onAction[actionName].iterables[iterable.name] = {};
                }
                eventSetReference.onAction[actionName].iterables[iterable.name][term.name] = eventSet;
            }
        }
    }
    return {
        onReadyEvents,
        onTermInputEvents,
        onIterableCountEvents,
        onActionEvents,
        eventSetReference,
    };
}
function extractDependenciesFromStatements(statements, thisContext) {
    const dependencies = [];
    for (const statement of statements) {
        if ((statement.type === "term-event-suppose" ||
            statement.type === "term-event-when") &&
            statement.condition.type === "syntax-tree") {
            dependencies.push(...(0, extract_dependencies_1.extractDependencies)(statement.condition.tree, thisContext));
        }
        if (statement.type === "term-event-set" &&
            statement.value.type === "syntax-tree") {
            dependencies.push(...(0, extract_dependencies_1.extractDependencies)(statement.value.tree, thisContext));
        }
    }
    return dependencies;
}
function parseTermPath(termPath, iterableContext) {
    const parts = termPath.split("/");
    if (parts.length > 2) {
        throw new Error(`Invalid term path ${termPath}. Term paths can only have one slash.`);
    }
    if (parts.length === 1) {
        return [iterableContext, parts[0]];
    }
    const [iterableName, termName] = parts;
    return [iterableName, termName];
}
//# sourceMappingURL=group-events.js.map